import { useEffect, useRef } from "react";

/**
 * AdSlot — Configurable advertisement placement component.
 * 
 * Supports two modes:
 * 1. Google AdSense (when REACT_APP_ADSENSE_ID is set)
 * 2. Internal house ads (default fallback — promote Shweebum features)
 * 
 * Usage: <AdSlot slot="sidebar-1" format="rectangle" />
 * 
 * To connect Google AdSense later:
 * 1. Add REACT_APP_ADSENSE_ID=ca-pub-XXXXXXX to frontend/.env
 * 2. Add the AdSense script tag to public/index.html
 * 3. The component will automatically switch from house ads to AdSense
 */

const ADSENSE_ID = process.env.REACT_APP_ADSENSE_ID;

// House ads — shown when no AdSense is configured
const HOUSE_ADS = [
  {
    id: "upgrade-storyteller",
    title: "Write Your First Book",
    description: "150,000 words/mo. AI Voice Reader. Story Doctor.",
    cta: "Try Shweebum Plus — $10/mo",
    link: "/pricing",
    bg: "from-slate-800/80 to-slate-700/60",
    border: "border-cyan-500/20"
  },
  {
    id: "upgrade-author",
    title: "Go Pro, Write More",
    description: "Unlimited projects. Full book generation. Your writing voice, amplified.",
    cta: "Shweebum Premium — $20/mo",
    link: "/pricing",
    bg: "from-purple-900/80 to-purple-800/60",
    border: "border-purple-500/30"
  },
  {
    id: "voice-reader",
    title: "Hear Your Story Come Alive",
    description: "9 expressive AI voices. Emotional narration. Free with any plan.",
    cta: "Start Writing",
    link: "/pricing",
    bg: "from-blue-900/80 to-blue-800/60",
    border: "border-blue-500/30"
  },
  {
    id: "upload-book",
    title: "Already Writing?",
    description: "Upload your manuscript. Get AI diagnosis & suggestions in seconds.",
    cta: "Upload Your Book",
    link: "/pricing",
    bg: "from-emerald-900/80 to-emerald-800/60",
    border: "border-emerald-500/30"
  }
];

const AdSlot = ({ slot = "default", format = "banner", className = "" }) => {
  const adRef = useRef(null);
  const houseAd = HOUSE_ADS[Math.floor(Math.random() * HOUSE_ADS.length)];

  useEffect(() => {
    // If AdSense is configured, try to initialize ad
    if (ADSENSE_ID && window.adsbygoogle) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        // AdSense blocked or not loaded — fallback to house ad
      }
    }
  }, []);

  // Dimensions based on format
  const formatStyles = {
    banner: "h-[90px] max-w-[728px]",
    rectangle: "h-[250px] max-w-[300px]",
    leaderboard: "h-[90px] w-full",
    sidebar: "h-[600px] max-w-[160px]",
    inline: "h-[100px] w-full"
  };

  const sizeClass = formatStyles[format] || formatStyles.banner;

  // Google AdSense mode
  if (ADSENSE_ID) {
    return (
      <div className={`flex justify-center my-4 ${className}`} data-testid={`ad-slot-${slot}`}>
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client={ADSENSE_ID}
          data-ad-slot={slot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
    );
  }

  // House ad mode (default)
  return (
    <div className={`flex justify-center my-4 ${className}`} data-testid={`ad-slot-${slot}`}>
      <a
        href={houseAd.link}
        className={`block ${sizeClass} w-full rounded-lg border ${houseAd.border} bg-gradient-to-r ${houseAd.bg} p-4 flex items-center justify-between hover:opacity-90 transition-opacity cursor-pointer no-underline`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm truncate">{houseAd.title}</p>
          <p className="text-white/70 text-xs mt-0.5 line-clamp-2">{houseAd.description}</p>
        </div>
        <span className="ml-3 shrink-0 text-xs font-medium px-3 py-1.5 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors">
          {houseAd.cta}
        </span>
      </a>
    </div>
  );
};

export default AdSlot;
